import { $source } from "../discover";
import { $notams, $signal, $theme } from "../store";

export function renderHeader(host: HTMLElement) {
  host.innerHTML = `
    <div class="header-band"></div>
    <div class="brand"><i class="ph-fill ph-airplane-tilt"></i>Octa Feeder Map</div>
    <div class="topbar">
      <div class="pill">
        <i class="ph ph-globe-hemisphere-east"></i>
        <span style="color:var(--muted)">JST</span><b id="jst" style="font-weight:500;font-variant-numeric:tabular-nums">--:--</b>
        <span class="divider"></span>
        <span style="color:var(--muted)">UTC</span><b id="utc" style="font-weight:500;font-variant-numeric:tabular-nums">--:--</b>
      </div>
      <div class="pill">
        <span class="dot" id="signalDot"></span>
        <span id="signalLabel">CONNECTING…</span>
        <span class="divider"></span>
        <i class="ph ph-cell-signal-full"></i>
        <span id="signalDb" style="font-variant-numeric:tabular-nums">— dB</span>
        <span class="divider"></span>
        <i class="ph ph-airplane-tilt"></i>
        <span id="signalCount" style="font-variant-numeric:tabular-nums" hidden></span>
        <span class="divider"></span>
        <button class="iconbtn" id="sourceBtn" aria-label="Data source" title=""><i class="ph ph-cloud" id="sourceIcon"></i></button>
        <button class="iconbtn notam-pill" id="notamBtn" aria-label="NOTAMs" title="NOTAMs"><i class="ph ph-warning-circle"></i><span id="notamBadge" class="badge" hidden>0</span></button>
        <span class="divider"></span>
        <button class="iconbtn" id="themeBtn" aria-label="Toggle theme"><i class="ph ph-moon" id="themeIcon"></i></button>
      </div>
    </div>
  `;

  /* ── Theme ─────────────────────────────────── */
  const themeBtn = host.querySelector("#themeBtn") as HTMLButtonElement;
  const themeIcon = host.querySelector("#themeIcon") as HTMLElement;
  function syncThemeIcon() {
    themeIcon.className = $theme.get() === "dark" ? "ph ph-moon" : "ph ph-sun";
  }
  themeBtn.addEventListener("click", () => {
    $theme.set($theme.get() === "dark" ? "light" : "dark");
    syncThemeIcon();
  });
  syncThemeIcon();

  /* ── Clocks ────────────────────────────────── */
  const jstEl = host.querySelector("#jst") as HTMLElement;
  const utcEl = host.querySelector("#utc") as HTMLElement;
  function fmt(d: Date, tzMin: number) {
    const t = new Date(d.getTime() + d.getTimezoneOffset() * 60000 + tzMin * 60000);
    return `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
  }
  function tick() {
    const now = new Date();
    jstEl.textContent = fmt(now, 9 * 60);
    utcEl.textContent = fmt(now, 0);
  }
  tick();
  setInterval(tick, 1000);

  /* ── Signal status ─────────────────────────── */
  const dbEl = host.querySelector("#signalDb") as HTMLElement;
  const countEl = host.querySelector("#signalCount") as HTMLElement;
  const labelEl = host.querySelector("#signalLabel") as HTMLElement;
  const dotEl = host.querySelector("#signalDot") as HTMLElement;
  $signal.subscribe((s) => {
    // Pi adsb-streamer reports dBFS (always negative). Older mock + remote
    // sources reported a positive dB value. Show whichever finite number we
    // get; only fall back to "—" when truly missing/NaN.
    dbEl.textContent = Number.isFinite(s.dB) ? `${s.dB.toFixed(1)} dB` : "— dB";
    // Only reveal the count once we have a real, non-zero value, so
    // there is no "0" flash between mount and the first
    // /pifeeder/info poll.
    if (s.trackedCount > 0) {
      countEl.textContent = String(s.trackedCount);
      countEl.hidden = false;
    } else {
      countEl.hidden = true;
    }
    labelEl.textContent = s.online ? "ONLINE" : "OFFLINE";
    dotEl.style.background = s.online ? "var(--accent-green)" : "var(--muted)";
  });

  /* ── Data source pill ──────────────────────── */
  const sourceBtn = host.querySelector("#sourceBtn") as HTMLButtonElement;
  const sourceIcon = host.querySelector("#sourceIcon") as HTMLElement;
  $source.subscribe((src) => {
    const cfg: Record<typeof src.kind, { icon: string; title: string }> = {
      app: { icon: "ph ph-device-mobile", title: "Embedded in pi-feeder-app" },
      local: { icon: "ph ph-wifi-high", title: `LAN feeder${"baseUrl" in src ? ` (${src.baseUrl})` : ""}` },
      api: { icon: "ph ph-cloud", title: "Remote pifeeder-api" },
      "dev-mock": { icon: "ph ph-flask", title: "Dev mock (vite dev) — fake aircraft + real DB" },
      mock: { icon: "ph ph-flask", title: "Mock data (no server)" },
    };
    const c = cfg[src.kind];
    sourceIcon.className = c.icon;
    sourceBtn.title = c.title;
  });

  /* ── NOTAM badge ───────────────────────────── */
  const notamBtn = host.querySelector("#notamBtn") as HTMLButtonElement;
  const badge = host.querySelector("#notamBadge") as HTMLElement;
  $notams.subscribe((rows) => {
    if (rows.length === 0) {
      badge.hidden = true;
      notamBtn.classList.remove("has-crit", "has-warn");
      return;
    }
    badge.hidden = false;
    badge.textContent = String(rows.length);
    const crit = rows.some((r) => r.severity === "crit");
    const warn = rows.some((r) => r.severity === "warn");
    notamBtn.classList.toggle("has-crit", crit);
    notamBtn.classList.toggle("has-warn", !crit && warn);
  });
  notamBtn.addEventListener("click", () => {
    document.dispatchEvent(new Event("pifeeder:toggle-notams"));
  });
}
